import { usePdfAsImage } from 'features/knowledgeDocumentViewer/components/viewer/image/hooks/usePdfAsImage'
import { cantileverDocumentPageIdForPage } from 'features/knowledgeDocumentViewer/utils/cantileverHostIds'
import {
  getDocumentImageQuery,
  resolvePageImageFileRef,
  resolvePageImageFileRefDual,
  resolvePdfFileRefDual,
} from 'features/knowledgeDocumentViewer/utils/documentPageImages'
import { useRolodexSignedURLs } from 'features/rolodex'
import { useMemo } from 'react'

interface UseDocumentPageImageOptions {
  /** Rolodex system-document entry id (develop navigation identity). */
  systemDocumentEntryId: string | undefined
  /** Known cantilever-document-page host (viewer tag / context). */
  cantileverDocumentPageId?: string
  cantileverDocumentId?: string
  /** Site id for UUID5 cantilever page-host derive. */
  siteId?: string
  /** Bare BQ document_id for UUID5 derive. */
  bqDocumentId?: string
  filename?: string
  skipHiRes?: boolean
  pageNumber?: number
}

interface UseDocumentPageImageResult {
  thumbnailUrl: string | undefined
  isThumbLoading: boolean
  hiResUrl: string | null
  isHiResLoading: boolean
}

/**
 * Page images: prefer known/derived cantilever-document-page host, then legacy
 * `${systemDocumentEntryId}_pg_N` on 404. List (skipHiRes) disables PDF fallback.
 */
export const useDocumentPageImage = ({
  systemDocumentEntryId,
  cantileverDocumentPageId,
  cantileverDocumentId,
  siteId,
  bqDocumentId,
  filename,
  skipHiRes = false,
  pageNumber = 1,
}: UseDocumentPageImageOptions): UseDocumentPageImageResult => {
  const isPdf = filename?.toLowerCase().endsWith('.pdf') ?? false

  const derivedCantileverPageId = useMemo(() => {
    if (!siteId || !bqDocumentId) return undefined
    return cantileverDocumentPageIdForPage(siteId, bqDocumentId, pageNumber)
  }, [bqDocumentId, pageNumber, siteId])

  // Prefer explicitly known host (viewer context), else UUID5 derive.
  const primaryCantileverPageId =
    cantileverDocumentPageId ?? derivedCantileverPageId

  const primaryThumbQuery = useMemo(() => {
    if (primaryCantileverPageId) {
      return resolvePageImageFileRef(primaryCantileverPageId, 'thumbnail')
    }
    if (systemDocumentEntryId) {
      return getDocumentImageQuery(
        systemDocumentEntryId,
        pageNumber,
        'thumbnail'
      )
    }
    return undefined
  }, [pageNumber, primaryCantileverPageId, systemDocumentEntryId])

  const { url: primaryThumbUrl, isLoading: isPrimaryThumbLoading } =
    useRolodexSignedURLs(primaryThumbQuery)

  const usedCantileverPrimary = !!primaryCantileverPageId
  const primaryThumb404 =
    usedCantileverPrimary && !isPrimaryThumbLoading && !primaryThumbUrl

  const legacyThumbFallbackQuery = useMemo(
    () =>
      primaryThumb404 && systemDocumentEntryId
        ? getDocumentImageQuery(systemDocumentEntryId, pageNumber, 'thumbnail')
        : undefined,
    [pageNumber, primaryThumb404, systemDocumentEntryId]
  )
  const { url: legacyThumbUrl, isLoading: isLegacyThumbLoading } =
    useRolodexSignedURLs(legacyThumbFallbackQuery)

  const hiResPrimaryQuery = useMemo(() => {
    if (skipHiRes) return undefined
    if (primaryCantileverPageId) {
      return resolvePageImageFileRef(primaryCantileverPageId, 'hiRes')
    }
    return resolvePageImageFileRefDual(
      systemDocumentEntryId,
      pageNumber,
      'hiRes'
    )
  }, [pageNumber, primaryCantileverPageId, skipHiRes, systemDocumentEntryId])

  const { url: hiResPrimaryUrl, isLoading: isHiResPrimaryLoading } =
    useRolodexSignedURLs(hiResPrimaryQuery)

  const primaryHiRes404 =
    !skipHiRes &&
    usedCantileverPrimary &&
    !isHiResPrimaryLoading &&
    !hiResPrimaryUrl

  const legacyHiResFallbackQuery = useMemo(
    () =>
      primaryHiRes404 && systemDocumentEntryId
        ? getDocumentImageQuery(systemDocumentEntryId, pageNumber, 'hiRes')
        : undefined,
    [pageNumber, primaryHiRes404, systemDocumentEntryId]
  )
  const { url: legacyHiResUrl, isLoading: isLegacyHiResLoading } =
    useRolodexSignedURLs(legacyHiResFallbackQuery)

  const thumbnailUrl = primaryThumbUrl ?? legacyThumbUrl
  const isThumbUrlLoading =
    isPrimaryThumbLoading ||
    (!!legacyThumbFallbackQuery && isLegacyThumbLoading)

  const hiResUrl = skipHiRes
    ? null
    : (hiResPrimaryUrl ?? legacyHiResUrl ?? null)
  const isHiResUrlLoading = skipHiRes
    ? false
    : isHiResPrimaryLoading ||
      (!!legacyHiResFallbackQuery && isLegacyHiResLoading)

  const thumbnail404 = !isThumbUrlLoading && !thumbnailUrl
  const hiRes404 = !skipHiRes && !isHiResUrlLoading && !hiResUrl
  // List thumbs (skipHiRes) must not convert PDFs on 404.
  const pdfFallbackNeeded =
    !skipHiRes &&
    isPdf &&
    !!filename &&
    (!!cantileverDocumentId || !!systemDocumentEntryId) &&
    (thumbnail404 || hiRes404)

  const pdfQuery = useMemo(
    () =>
      pdfFallbackNeeded
        ? resolvePdfFileRefDual(
            systemDocumentEntryId,
            filename,
            cantileverDocumentId
          )
        : undefined,
    [cantileverDocumentId, filename, pdfFallbackNeeded, systemDocumentEntryId]
  )
  const { url: pdfSignedUrl, isLoading: isPdfUrlLoading } =
    useRolodexSignedURLs(pdfQuery)
  const { imageUrl: pdfImageUrl, isLoading: isPdfConvertLoading } =
    usePdfAsImage(
      pdfSignedUrl,
      pageNumber,
      skipHiRes ? 0.5 : 1,
      1,
      true,
      skipHiRes
        ? {
            preloadAdjacentPages: false,
            releaseDocumentAfterRender: true,
          }
        : undefined
    )
  const isPdfLoading = isPdfUrlLoading || isPdfConvertLoading

  return {
    thumbnailUrl: thumbnailUrl ?? pdfImageUrl ?? undefined,
    isThumbLoading:
      isThumbUrlLoading || (thumbnail404 && pdfFallbackNeeded && isPdfLoading),
    hiResUrl: skipHiRes ? null : (hiResUrl ?? pdfImageUrl ?? null),
    isHiResLoading: skipHiRes
      ? false
      : isHiResUrlLoading || (hiRes404 && pdfFallbackNeeded && isPdfLoading),
  }
}
